import { Button, Dialog, DialogContent, DialogTitle } from "@mui/material";
import { useState, type Dispatch, type FC } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useSupabaseCallback } from "../../../../server/supabase/hooks";
import { useDialog } from "../../../../utils/hooks/use-dialog";
import type { UUID } from "../../../../utils/uuid";
import { SegmentSelect } from "../../../message/editor/plugins/dynamic-content-plugin/segment-select";
import { SupabaseSegmentService } from "../../server/supabase-segment-service";
import { useSegmentsWithFavourites } from "../../server/use-segments";
import type { SegmentManagerAction } from "./reducer/actions";
import type { Segment } from "./types";

export const AddFromExistingSegmentButton: FC<{
  dispatch: Dispatch<SegmentManagerAction<Segment>>;
  disabled: boolean;
}> = ({ dispatch, disabled }) => {
  const dialog = useDialog();
  const [loading, setLoading] = useState(false);

  const segmentLoader = useSegmentsWithFavourites({
    include_deleted: false,
    is_recipient_type: false,
  });

  const handleSelect = useSupabaseCallback(
    async ({ supabase }, id: UUID) => {
      if (!id) return;
      setLoading(true);

      const resp = await new SupabaseSegmentService(supabase).getSegmentData(id);

      if (resp.error) {
        setLoading(false);
        console.log(resp.error);
        dispatch({ type: "ADD_GROUP" });
        return;
      }

      const segment = resp.data;

      dispatch({ type: "ADD_GROUP", payload: segment.segment_json.segments });

      setLoading(false);
    },
    [setLoading, dispatch],
  );

  const { t } = useTranslation();

  return (
    <>
      <Button
        variant="text"
        onClick={dialog.handleOpen}
        data-analytics-id="add-from-segment-button"
        sx={{ maxWidth: "fit-content" }}
        disabled={disabled || segmentLoader.loading || loading}
      >
        {t("or create a group based on an existing segment")}
      </Button>
      <Dialog open={dialog.open} onClose={dialog.handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t("Select a segment to base your recipient group on")}</DialogTitle>
        <DialogContent>
          <SegmentSelect
            segmentLoader={segmentLoader}
            disabled={segmentLoader.loading || loading}
            loading={segmentLoader.loading || loading}
            onChange={(id) => {
              if (id) {
                void handleSelect(id)
                  .then(() => dialog.handleClose())
                  .catch((err) => {
                    console.error(err);
                    toast.error(t("Unable to add segment"));
                  });
              }
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};
